import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Store, MapPin, Phone, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_LABELS = {
  mayo: { fr: 'Mayo épicée', en: 'Spicy Mayo' },
  bbq: { fr: 'Sauce BBQ', en: 'BBQ Sauce' },
  poutine_sauce: { fr: 'Sauce à poutine', en: 'Poutine Gravy' },
  other: { fr: 'Autres produits', en: 'Other products' },
};

export default function RetailersPage() {
  const { lang } = useLanguage();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/retail-locations`).then(r => {
      setLocations(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Group by product type
  const grouped = locations.reduce((acc, loc) => {
    const key = loc.product_type || 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(loc);
    return acc;
  }, {});

  const order = ['mayo', 'bbq', 'poutine_sauce', 'other'];

  return (
    <div className="pt-20 lg:pt-24 pb-20" data-testid="retailers-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl font-bold text-[#1a1a1a] tracking-tight"
          >
            {lang === 'fr' ? 'Nos produits en vente' : 'Our products in stores'}
          </motion.h1>
          <p className="text-[#555555] mt-3 text-base">
            {lang === 'fr' ? 'Trouvez nos sauces signature chez ces détaillants' : 'Find our signature sauces at these retailers'}
          </p>
          <div className="w-16 h-1 mx-auto mt-4 bg-[#e53a3a]" />
        </div>

        {loading ? (
          <div className="text-center py-20 text-[#555555]">{lang === 'fr' ? 'Chargement...' : 'Loading...'}</div>
        ) : locations.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#e0e0e0] p-12 text-center">
            <Store className="w-12 h-12 mx-auto text-[#bbb] mb-4" />
            <h3 className="font-heading text-xl font-semibold text-[#1a1a1a] mb-2">
              {lang === 'fr' ? 'Bientôt en magasin' : 'Coming soon to stores'}
            </h3>
            <p className="text-sm text-[#555555]">
              {lang === 'fr' ? 'Les points de vente seront annoncés sous peu.' : 'Retail locations will be announced shortly.'}
            </p>
          </div>
        ) : (
          <div className="space-y-12">
            {order.filter(k => grouped[k]?.length > 0).map(key => {
              const items = grouped[key] || [];
              const sample = items[0];
              const productName = (lang === 'fr' ? sample.product_name_fr : sample.product_name_en) || PRODUCT_LABELS[key][lang];
              return (
                <section key={key} data-testid={`product-section-${key}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-8 bg-[#e53a3a] rounded-full" />
                    <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{productName}</h2>
                    <span className="text-sm text-[#888]">({items.length})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map(loc => (
                      <motion.div
                        key={loc.id}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="bg-white rounded-xl border border-[#e0e0e0] p-5 hover:shadow-lg transition-shadow"
                        data-testid={`retailer-card-${loc.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#f4d00d] flex items-center justify-center shrink-0">
                            <Store className="w-5 h-5 text-black" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading text-lg font-semibold text-[#1a1a1a] truncate">{loc.retailer_name}</h3>
                            {(loc.address || loc.city) && (
                              <p className="text-xs text-[#555555] mt-1 flex items-center gap-1">
                                <MapPin className="w-3 h-3 shrink-0" />
                                <span>{[loc.address, loc.city].filter(Boolean).join(', ')}</span>
                              </p>
                            )}
                            {loc.phone && (
                              <a href={`tel:${loc.phone}`} className="text-xs text-[#e53a3a] hover:underline mt-1 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {loc.phone}
                              </a>
                            )}
                            {loc.website && (
                              <a href={loc.website} target="_blank" rel="noopener noreferrer" className="text-xs text-[#e53a3a] hover:underline mt-1 flex items-center gap-1">
                                <Globe className="w-3 h-3" /> {lang === 'fr' ? 'Site web' : 'Website'}
                              </a>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
